import { Suspense, lazy } from 'react';
import { Toaster } from './components/ui/toaster';
import { Toaster as Sonner } from './components/ui/sonner';
import { TooltipProvider } from './components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { UserWrapper } from './app/shared/components/UserWrapper';
import { Layout } from './app/shared/components/layout/Layout';
import ProtectedRoute from './app/shared/components/ProtectedRoute';
import { Loader2 } from 'lucide-react';

// Lazy load module routes
const AdminRoutes = lazy(() =>
  import('./app/admin/routes').then(m => ({ default: m.AdminRoutes }))
);

// Lazy load shared pages
const Login = lazy(() => import('./app/shared/pages/Login'));
const NotFound = lazy(() => import('./app/shared/pages/NotFound'));

// Lazy load user pages directly (since user routes need different path structure)
const UserDashboard = lazy(() => import('./app/user/pages/UserDashboard'));
const ProductDetails = lazy(() => import('./app/user/pages/ProductDetails'));

const queryClient = new QueryClient();

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground">Loading page...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />

              {/* Admin routes - use modular routing */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Layout>
                      <AdminRoutes />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* User routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requiredRole="user">
                    <UserWrapper>
                      <Layout>
                        <UserDashboard />
                      </Layout>
                    </UserWrapper>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product/:id"
                element={
                  <ProtectedRoute requiredRole="user">
                    <UserWrapper>
                      <Layout>
                        <ProductDetails />
                      </Layout>
                    </UserWrapper>
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
